import { useCallback } from "react";

import { ChevronDown } from "components/icons/generated";
import Box from "ds/components/Box";
import Icon from "ds/components/Icon";
import useTypedContext from "hooks/useTypedContext";
import { pluralize } from "shared/Pluralize";

import { CodeChangesContext } from "../CodeChangesContext";
import { braceClose, braceOpen, bracketClose, bracketOpen, ellipsis } from "../constants";
import { hasChildren, isArray } from "../helpers";
import styles from "../styles.module.css";
import { CodeChangesChangeItem } from "../types";
import Label from "./Label";
import Row from "./Row";

type GroupProps = {
  item: CodeChangesChangeItem;
};

const Group = ({ item }: GroupProps) => {
  const { collapsedGroups, toggleGroup } = useTypedContext(CodeChangesContext);

  const handleExpansion = useCallback(() => {
    toggleGroup(item.id);
  }, [item.id, toggleGroup]);

  const handleExpansionKeyboard = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.code === "Space") {
        handleExpansion();
      }
    },
    [handleExpansion]
  );

  const isCollapsed = collapsedGroups.has(item.id);
  const childIsArray = isArray(item);
  const openGroup = childIsArray ? bracketOpen : braceOpen;
  const closeGroup = childIsArray ? bracketClose : braceClose;

  return (
    <>
      {hasChildren(item) ? (
        <>
          <Box
            align="center"
            gap="medium"
            className={styles.codeChangesGroupLabelRow}
            tabIndex={0}
            onKeyDown={handleExpansionKeyboard}
            onClick={handleExpansion}
            role="button"
            aria-expanded={!isCollapsed}
          >
            <Icon
              className={styles.codeChangesChevron}
              src={ChevronDown}
              rotate={isCollapsed ? "270" : undefined}
            />
            <Label keyName={item.key} />
            <span>{openGroup}</span>
            {isCollapsed && (
              <>
                <span className={styles.codeChangesEllipsis}>{ellipsis}</span>
                <span>{closeGroup}</span>
                <span className={styles.codeChangesGroupCount}>
                  {item.children.length} {pluralize("item", item.children.length)}
                </span>
              </>
            )}
          </Box>
          {!isCollapsed && (
            <>
              <Box
                direction="column"
                gap="medium"
                margin="0 0 0 small"
                padding="0 0 0 x-large"
                className={styles.codeChangesGroupContent}
              >
                {item.children.map((child) => (
                  <Group key={child.id} item={child} />
                ))}
              </Box>
              <div className={styles.codeChangesGroupClosingRow}>
                <span>{closeGroup}</span>
              </div>
            </>
          )}
        </>
      ) : (
        // TODO: Study using subgrid to align the values in the row
        <Row item={item} />
      )}
    </>
  );
};

export default Group;
